import type { Task } from '@/types/task';
import { getTaskStatus } from '@/utils/task-display-utils';

// ─────────────────────────────────────────────────────────────────────────────
// Single-precedence invariant (DASH-P1 / M97, AC4 / D-3)
//
// `getTaskStatus` (src/utils/task-display-utils.ts:10-16) is the ONE status
// classifier for BOTH the status-line breakdown counts (overdue / open) AND the
// surfaced row stripes. This module never reimplements that precedence — every
// classification below routes through `getTaskStatus`.
//
// The done/total numerator is NOT computed here. It is supplied separately by
// `liveTaskStats` (DashboardPage.tsx:385-419), the SSOT reactive numerator that
// also drives CountdownWidget's N/N display. This module does not count
// completed/total over a task list.
//
// Divergence this card sidesteps (F-3): `SeasonTaskStats.overdue`
// (dashboard-service.ts:97) counts a snoozed-but-past-due task as overdue (it has
// no snooze check), whereas `getTaskStatus` gives snooze precedence over overdue.
// By deriving the status-line `overdue` count from `getTaskStatus` here (not from
// the snapshot field), the status line and the row stripes agree by construction.
// The dashboard-service snapshot-overdue divergence is tracked separately (#858);
// this card does not touch dashboard-service.
// ─────────────────────────────────────────────────────────────────────────────

/** Max actionable rows surfaced in the expanded state (DASH-P1 fixed top-3). */
const SURFACED_TASK_LIMIT = 3;

/**
 * Selects the up-to-3 actionable open tasks to surface in the expanded card.
 *
 * Rules (via the SHARED `getTaskStatus` classifier — no precedence reimplemented):
 *   - Exclude DONE / BLOCKED / SNOOZED (only 'overdue' and 'pending' survive).
 *   - Pin OVERDUE first.
 *   - Then soonest `dueDate` ascending.
 *   - Then `sortOrder` ascending.
 *   - Take the first 3.
 *
 * `Task.dueDate` is required (types/task.ts:39, D-4) so the comparator always has
 * a value — no no-dueDate fallback branch.
 */
export function deriveSurfacedTasks(activeSeasonTasks: Task[], today: string): Task[] {
  const actionable = activeSeasonTasks.filter((task) => {
    const status = getTaskStatus(task, today);
    return status === 'overdue' || status === 'pending';
  });

  const sorted = [...actionable].sort((a, b) => {
    const aOverdue = getTaskStatus(a, today) === 'overdue';
    const bOverdue = getTaskStatus(b, today) === 'overdue';
    if (aOverdue !== bOverdue) return aOverdue ? -1 : 1;

    if (a.dueDate !== b.dueDate) return a.dueDate < b.dueDate ? -1 : 1;

    return a.sortOrder - b.sortOrder;
  });

  return sorted.slice(0, SURFACED_TASK_LIMIT);
}

export interface StatusLineCounts {
  /** Count of tasks classified 'overdue' by `getTaskStatus`. */
  overdue: number;
  /** Count of tasks classified 'pending' (actionable, not yet due) by `getTaskStatus`. */
  open: number;
  /**
   * Whole-day lead time from `today` to the soonest future `dueDate` among OPEN
   * (pending) tasks; null when no pending task has a future due date.
   */
  nextDueLeadDays: number | null;
}

/**
 * Derives the collapsed-state status-line counts, all via the SAME `getTaskStatus`
 * classifier the row stripes use (so the two surfaces agree by construction).
 *
 *   - `overdue` = count where `getTaskStatus === 'overdue'`.
 *   - `open`    = count where `getTaskStatus === 'pending'`.
 *   - `nextDueLeadDays` = (soonest future `dueDate` among OPEN tasks) − today, in
 *     whole days; null when no pending task is due in the future.
 */
export function deriveStatusLineCounts(activeSeasonTasks: Task[], today: string): StatusLineCounts {
  let overdue = 0;
  let open = 0;
  let soonestFutureDue: string | null = null;

  for (const task of activeSeasonTasks) {
    const status = getTaskStatus(task, today);
    if (status === 'overdue') {
      overdue += 1;
    } else if (status === 'pending') {
      open += 1;
      // pending ⇒ dueDate >= today (overdue would have been classified otherwise).
      // Only count a strictly-future due date toward "next due in {d}d".
      if (task.dueDate > today && (soonestFutureDue === null || task.dueDate < soonestFutureDue)) {
        soonestFutureDue = task.dueDate;
      }
    }
  }

  const nextDueLeadDays =
    soonestFutureDue === null ? null : diffInWholeDays(today, soonestFutureDue);

  return { overdue, open, nextDueLeadDays };
}

/**
 * Whole-day difference between two ISO `YYYY-MM-DD` date strings (`to` − `from`).
 * UTC-anchored so it is DST-stable and matches the date-only `today` slicing used
 * across the dashboard.
 */
function diffInWholeDays(from: string, to: string): number {
  const fromMs = Date.parse(`${from}T00:00:00.000Z`);
  const toMs = Date.parse(`${to}T00:00:00.000Z`);
  return Math.round((toMs - fromMs) / 86_400_000);
}
